import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Play, ArrowUp, ArrowDown, ArrowUpToLine, ArrowDownToLine, Megaphone, Zap, MapPin, Coffee, Maximize2 } from 'lucide-react';
import { activePhases, buildQueue, forecastQueue, isPaused, matchCourtGroup, nextMatchForCourt, suggestAssignments, type Court, type Match } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { Button, Dialog, Empty, Input, SectionHead, Select, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { WhoPlaysWhen } from '@/components/WhoPlaysWhen';
import { TeamPartien, zuTeamPartien } from '@/components/TeamPartien';
import { ResultDialog } from '@/components/ResultDialog';
import { LiveScore } from '@/components/LiveScore';
import { MatchRow } from '@/components/MatchCard';
import { ReportsPanel } from '@/components/ReportsPanel';
import { PhaseAbschlussLeiste } from '@/components/PhaseAbschluss';
import { fmtDuration, useNow } from '@/hooks/useNow';
import { courtLabel, feldName, matchContext, scoreText, slotLabel, slotTeam, teamLookup } from '@/lib/teams';
import { fmtTime } from '@/lib/format';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';

/**
 * Der Turniertags-Screen (docs/06 §6, docs/11 §5.5): Feld-Kacheln oben,
 * Warteschlange rechts, Beendete unten. Feld frei → nächstes Spiel rückt nach.
 *
 * Plätze (docs/17): Haben Felder eine Gruppe („Piazza", „Sportplatz"), kann
 * die Ansicht auf einen Platz beschränkt werden — die Turnierleitung Platz 2
 * sieht dann nur ihre Felder und ihre Spiele. Die Vorschau („Wann spielt
 * wer?") rechnet die Warteschlange nach vorn, damit Spieler wissen, wann
 * ungefähr ihre nächste Partie ansteht.
 */
export function SpielePage() {
  const t = useT();
  // blockedBy kommt deutsch aus der Engine; Vergleiche bleiben auf dem Original
  const tm = useTm();
  const { tournament, dispatch } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();
  const lookup = useMemo(() => teamLookup(comp), [comp]);
  const now = useNow(5_000);
  const [resultMatch, setResultMatch] = useState<Match | null>(null);
  // Partien eines Teams unter „Beendet“ (Harald, 10/2026) — `teamId` gesetzt, wenn bei „Wann spielt wer?“ ein Team angeklickt wurde
  const [partienSuche, setPartienSuche] = useState<{ q: string; teamId?: string }>({ q: '' });
  const sucheTeam = (q: string, teamId?: string) => setPartienSuche(teamId ? { q, teamId } : { q });
  const [queueBig, setQueueBig] = useState(false);
  const [pickFor, setPickFor] = useState<Court | null>(null);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseUntil, setPauseUntil] = useState('');
  /*
   * Auto-Zuweisung läuft auf **einem** Gerät. Vorher tat es jedes: Leitung und
   * jeder Helfer rechneten nach jedem Abgleich dieselbe Zuweisung, starteten
   * dasselbe Spiel, und die Kollisionen mussten hinterher als „überholt"
   * aufgeräumt werden — die Ursache der meisten Konfliktmeldungen. Helfer
   * weisen nie zu; ein per Link beigetretenes Leitungsgerät (`autoLeader:
   * false`) erst, wenn man es hier einschaltet. Gemerkt wird das je Turnier.
   */
  const helferGeraet = sync.cfg?.role === 'helfer';
  const autoVorgabe = !helferGeraet && sync.cfg?.autoLeader !== false;
  const autoKey = `airmail.auto.${tournament.id}`;
  const [auto, setAuto] = useState(() => {
    if (helferGeraet) return false;
    const gemerkt = localStorage.getItem(autoKey);
    return gemerkt === null ? autoVorgabe : gemerkt !== '0';
  });
  const [callouts, setCallouts] = useState<{ id: string; feld: string; paarung: string; at: number }[]>([]);
  const lastLog = useRef(tournament.log.length);

  useEffect(() => {
    if (!helferGeraet) localStorage.setItem(autoKey, auto ? '1' : '0');
  }, [auto, autoKey, helferGeraet]);

  // Platz-Filter: je Turnier gemerkt (Gerät der Turnierleitung Platz 2 bleibt auf „seinem" Platz)
  const venueKey = `airmail.venue.${tournament.id}`;
  const courtGroups = useMemo(() => [...new Set(tournament.courts.filter((c) => c.active && c.group).map((c) => c.group as string))].sort((a, b) => a.localeCompare(b)), [tournament.courts]);
  // Ein Helfer ist auf seinen Ort festgelegt (docs/notes/helfer.md) — der Filter
  // ist dann keine Wahl mehr, sondern seine Zuständigkeit
  const festerOrt = sync.cfg?.role === 'helfer' ? sync.cfg.courtGroup : undefined;
  const [venueState, setVenue] = useState(() => localStorage.getItem(venueKey) ?? '');
  const venue = festerOrt ?? venueState;
  useEffect(() => {
    if (!festerOrt && venueState && !courtGroups.includes(venueState)) setVenue('');
  }, [festerOrt, venueState, courtGroups]);
  useEffect(() => {
    if (festerOrt) return;
    if (venueState) localStorage.setItem(venueKey, venueState);
    else localStorage.removeItem(venueKey);
  }, [festerOrt, venueState, venueKey]);
  const onVenue = useCallback(
    (m: Match) => {
      if (!venue) return true;
      const g = matchCourtGroup(tournament, m);
      return !g || g === venue;
    },
    [tournament, venue],
  );

  const phases = activePhases(tournament);
  const allMatches = useMemo(() => comp.phases.flatMap((p) => p.matches), [comp]);
  const courts = useMemo(() => tournament.courts.filter((c) => c.active && (!venue || c.group === venue)), [tournament.courts, venue]);
  const courtIds = useMemo(() => new Set(courts.map((c) => c.id)), [courts]);
  const running = allMatches.filter((m) => m.status === 'running' && (!venue || (m.courtId && courtIds.has(m.courtId))));
  const fullQueue = useMemo(() => buildQueue(tournament, now), [tournament, now]);
  const queue = useMemo(() => fullQueue.filter((e) => onVenue(e.match)), [fullQueue, onVenue]);

  /*
   * Warteschlange von Hand ordnen (Daniel, 10/2026): ↑ eins hoch, ↓ eins
   * runter, ⤓ ans Ende — „falls jemand zu spät kommt" —, ⤒ ganz nach vorn.
   * Hoch/Runter tauscht mit dem Nachbarn in der sichtbaren Liste; über eine
   * Phasengrenze geht das nicht (die Phase zählt vor jeder Priorität).
   */
  const verschiebe = (i: number, richtung: -1 | 1) => {
    const j = i + richtung;
    const a = queue[i];
    const b = queue[j];
    if (!a || !b || a.match.phaseId !== b.match.phaseId) return;
    const ids = queue.slice(0, Math.max(i, j) + 1).map((x) => x.match.id);
    [ids[i], ids[j]] = [ids[j] as string, ids[i] as string];
    dispatch({ type: 'match.order', matchIds: ids });
  };
  const pfeile = (e: (typeof queue)[number], i: number) => {
    const knopf = 'text-dim hover:text-ink cursor-pointer inline-flex items-center justify-center p-1 max-md:min-w-11 max-md:min-h-11 disabled:opacity-25 disabled:cursor-default';
    const hoch = i > 0 && queue[i - 1]?.match.phaseId === e.match.phaseId;
    const runter = i < queue.length - 1 && queue[i + 1]?.match.phaseId === e.match.phaseId;
    const n = e.match.number;
    return (
      <span className="inline-flex items-center -m-1" data-warteschlange-pfeile>
        <button
          type="button"
          className={knopf}
          disabled={!hoch}
          title={t('Ganz nach vorn')}
          aria-label={t('Spiel #{n} ganz nach vorn', { n })}
          onClick={() => dispatch({ type: 'match.setPriority', matchId: e.match.id, priority: Math.max(0, ...fullQueue.map((q) => q.match.priority)) + 1 })}
          data-pfeil="vorn"
        >
          <ArrowUpToLine size={14} />
        </button>
        <button type="button" className={knopf} disabled={!hoch} title={t('Eins nach oben')} aria-label={t('Spiel #{n} eins nach oben', { n })} onClick={() => verschiebe(i, -1)} data-pfeil="hoch">
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          className={knopf}
          disabled={!runter}
          title={t('Eins nach unten')}
          aria-label={t('Spiel #{n} eins nach unten', { n })}
          onClick={() => verschiebe(i, 1)}
          data-pfeil="runter"
        >
          <ArrowDown size={14} />
        </button>
        <button
          type="button"
          className={knopf}
          disabled={!runter}
          title={t('Ans Ende — z. B. wer zu spät kommt')}
          aria-label={t('Spiel #{n} ans Ende', { n })}
          onClick={() => dispatch({ type: 'match.setPriority', matchId: e.match.id, priority: Math.min(0, ...fullQueue.map((q) => q.match.priority)) - 1 })}
          data-pfeil="ende"
        >
          <ArrowDownToLine size={14} />
        </button>
      </span>
    );
  };
  const forecast = useMemo(() => new Map(forecastQueue(tournament, now).map((f) => [f.matchId, f])), [tournament, now]);
  const finished = allMatches
    .filter((m) => (m.status === 'finished' || m.status === 'walkover') && m.endedAt && onVenue(m))
    .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
    .slice(0, 12);

  // Auto-Zuweisung: sobald ein Feld frei ist und ein startbares Spiel existiert — nur auf den Feldern dieses Platzes
  useEffect(() => {
    if (!auto || phases.length === 0) return;
    const sugg = suggestAssignments(tournament, Date.now()).filter((s) => courtIds.has(s.courtId));
    if (sugg.length === 0) return;
    const texts: { id: string; feld: string; paarung: string; at: number }[] = [];
    for (const s of sugg) {
      const m = allMatches.find((x) => x.id === s.matchId);
      const c = tournament.courts.find((x) => x.id === s.courtId);
      if (m && c && dispatch({ type: 'match.start', matchId: s.matchId, courtId: s.courtId }, { silent: true }).ok) {
        texts.push({ id: m.id, feld: feldName(c.name), paarung: `${slotLabel(m.a, lookup, allMatches)} – ${slotLabel(m.b, lookup, allMatches)}`, at: Date.now() });
      }
    }
    if (texts.length) setCallouts((prev) => [...texts, ...prev].slice(0, 6));
  }, [tournament, auto, phases.length, courtIds, allMatches, lookup, dispatch]);

  // Team-Aufruf auch für manuell gestartete Spiele
  useEffect(() => {
    if (tournament.log.length <= lastLog.current) return;
    const fresh = tournament.log.slice(lastLog.current).filter((a) => a.type === 'match.start');
    lastLog.current = tournament.log.length;
    const texts = fresh.flatMap((a) => {
      if (a.type !== 'match.start') return [];
      const m = allMatches.find((x) => x.id === a.matchId);
      const c = tournament.courts.find((x) => x.id === a.courtId);
      if (!m || !c || !courtIds.has(c.id) || callouts.some((x) => x.id === m.id)) return [];
      return [{ id: m.id, feld: feldName(c.name), paarung: `${slotLabel(m.a, lookup, allMatches)} – ${slotLabel(m.b, lookup, allMatches)}`, at: a.at }];
    });
    if (texts.length) setCallouts((prev) => [...texts, ...prev].slice(0, 6));
    // `lastLog` hält den Effekt still, solange keine neue Aktion im Protokoll steht — die übrigen Werte lösen ihn nicht aus
  }, [tournament.log, tournament.courts, allMatches, courtIds, lookup, callouts]);

  if (phases.length === 0) {
    const anyPlanned = comp.phases.some((p) => p.status === 'planned');
    return (
      <div className="max-w-2xl">
        <SectionHead title={t('Spiele')} />
        <Empty title={comp.phases.every((p) => p.status === 'finished') && comp.phases.length > 0 ? t('Turnier beendet') : t('Keine laufende Phase')}>
          {anyPlanned ? (
            <>
              {t('Erst auslosen:')}{' '}
              <Link to="../vorrunde" className="text-blue underline">
                {t('Vorrunde')}
              </Link>{' '}
              {t('bzw.')}{' '}
              <Link to="../ko" className="text-blue underline">
                {t('KO-Runde')}
              </Link>
              .
            </>
          ) : (
            t('Alle Phasen sind abgeschlossen.')
          )}
        </Empty>
        {/* Auch nach Turnierende oder zwischen zwei Phasen: Ergebnisse eines Teams nachschlagen */}
        {allMatches.some((m) => m.status === 'finished' || m.status === 'walkover') && (
          <TeamPartien
            comp={comp}
            tournament={tournament}
            zuletzt={allMatches
              .filter((m) => (m.status === 'finished' || m.status === 'walkover') && m.endedAt)
              .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
              .slice(0, 12)}
            suche={partienSuche.q}
            teamId={partienSuche.teamId}
            onSuche={sucheTeam}
          />
        )}
      </div>
    );
  }

  const startOnCourt = (court: Court) => {
    const m = nextMatchForCourt(tournament, court.id, Date.now());
    if (!m) return setPickFor(court);
    dispatch({ type: 'match.start', matchId: m.id, courtId: court.id });
  };

  const open = queue.length;
  // Aufruf: nur Spiele, die noch laufen, höchstens 90 s alt, neuestes zuerst und hervorgehoben — nichts sammelt sich an
  const liveCallouts = callouts.filter((c) => now - c.at < 90_000 && running.some((m) => m.id === c.id)).slice(0, 3);
  const eta = (m: Match) => {
    const f = forecast.get(m.id);
    if (!f) return undefined;
    return { time: `~${fmtTime(f.startsAt)}`, court: courtLabel(tournament, f.courtId) };
  };
  const paused = isPaused(tournament, now);
  const startPause = (minutes?: number) => {
    let until: number | undefined;
    if (minutes) until = Date.now() + minutes * 60_000;
    else if (pauseUntil) {
      const [h, m] = pauseUntil.split(':').map(Number);
      const d = new Date();
      d.setHours(h ?? 0, m ?? 0, 0, 0);
      if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
      until = d.getTime();
    }
    if (!until) return;
    dispatch({ type: 'tournament.pause', until });
    setPauseOpen(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
      <div className="min-w-0">
        <SectionHead title={t('Felder')} meta={t('{n} aktiv · {r} laufen · {o} Spiele offen', { n: courts.length, r: running.length, o: open })}>
          {/* Festgelegter Ort: keine Auswahl, sondern eine Ansage */}
          {festerOrt && (
            <span className="label inline-flex items-center gap-1.5 border border-blue text-blue px-2 py-1">
              <MapPin size={12} /> {festerOrt}
            </span>
          )}
          {!festerOrt && courtGroups.length > 0 && (
            <label className="flex items-center gap-1.5 text-sm" title={t('Nur Felder und Spiele dieses Platzes zeigen — für die Turnierleitung vor Ort')}>
              <MapPin size={13} className="text-dim" />
              <Select className="w-auto py-1 text-sm" value={venue} onChange={(e) => setVenue(e.target.value)} aria-label={t('Platz')}>
                <option value="">{t('Alle Plätze')}</option>
                {courtGroups.map((g) => (
                  <option key={g} value={g}>
                    {t('Platz {g}', { g })}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {!helferGeraet && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" title={t('Nur ein Gerät sollte automatisch zuweisen — sonst starten zwei Geräte dasselbe Spiel.')}>
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
              <Zap size={13} className={cx(auto ? 'text-live' : 'text-dim')} /> {t('Auto-Zuweisung')}
            </label>
          )}
          {paused ? (
            <Button size="sm" variant="primary" onClick={() => dispatch({ type: 'tournament.pause' })}>
              <Coffee size={13} /> {t('Pause beenden')}
            </Button>
          ) : (
            <Button size="sm" onClick={() => setPauseOpen(true)} title={t('Mittagspause: Auto-Zuweisung ruht, Zeiten in der Vorschau verschieben sich')}>
              <Coffee size={13} /> {t('Pause')}
            </Button>
          )}
        </SectionHead>

        {/* Stand je laufender Phase und der Weg zum Abschluss — hier, wo man am Turniertag ist (docs/notes/phase-abschluss.md) */}
        {comp.phases
          .filter((p) => p.status === 'drawn' || p.status === 'running')
          .map((p) => (
            <PhaseAbschlussLeiste key={p.id} phase={p} />
          ))}

        {paused && (
          <div className="border border-warn text-warn px-4 py-2.5 mb-3 text-sm flex items-center gap-3">
            <Coffee size={15} className="shrink-0" />
            <span className="flex-1">
              {t('Pause bis')} <b>{fmtTime(tournament.pausedUntil)}</b>{' '}
              {t('— die Auto-Zuweisung ruht, „Wann spielt wer?“ rechnet ab dann. Ein Spiel lässt sich trotzdem von Hand starten („Anderes Spiel“).')}
            </span>
            <Button size="sm" onClick={() => dispatch({ type: 'tournament.pause' })}>
              {t('Pause beenden')}
            </Button>
          </div>
        )}

        <ReportsPanel />

        {liveCallouts.length > 0 && (
          <div className="bg-panel border border-line px-4 py-2.5 mb-3 flex items-center gap-3 overflow-hidden">
            <Megaphone size={16} className="text-warn shrink-0" />
            <div className="font-mono text-[13px] truncate min-w-0">
              <span className="label mr-2">{t('Aufruf')}</span>
              {/* Alle Aufrufe sehen gleich aus: Feld fett, Paarung normal. Früher
                  stand der jüngste hervorgehoben und die älteren blass — das las
                  sich wie ein Fehler, obwohl beide gerade gerufen werden. Neu
                  gegen alt unterscheidet die Reihenfolge, nicht die Farbe. */}
              {liveCallouts.map((c, i) => (
                <span key={c.id} className="text-ink">
                  {i > 0 && <span className="mx-2 text-dim">·</span>}
                  <b>{c.feld}</b>: {c.paarung}
                </span>
              ))}
            </div>
            <button className="ml-auto label cursor-pointer hover:text-ink shrink-0" onClick={() => setCallouts([])}>
              {t('leeren')}
            </button>
          </div>
        )}

        {courts.length === 0 && <div className="text-sm text-warn mb-3">{venue ? t('Kein aktives Feld auf Platz {v}.', { v: venue }) : t('Kein aktives Feld.')}</div>}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-3">
          {courts.map((court, i) => {
            const m = running.find((x) => x.courtId === court.id);
            const next = !m ? nextMatchForCourt(tournament, court.id, now) : undefined;
            const lastEnd = allMatches.filter((x) => x.courtId === court.id && x.endedAt).sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))[0]?.endedAt;
            return (
              // Die Einführung leuchtet das erste Feld aus, nicht die ganze Spalte — ein
              // bildschirmhoher Scheinwerfer lässt für die Erklärkarte keinen Platz übrig
              <section key={court.id} data-tour={i === 0 ? 'spiele-felder' : undefined} className={cx('bg-panel2 border', m ? 'border-line' : 'border-dashed border-line')}>
                <header className="flex items-center gap-2 px-4 py-2 border-b border-line2 font-mono text-[11.5px] tracking-[.08em] uppercase whitespace-nowrap overflow-hidden">
                  <b className="text-ink shrink-0">{feldName(court.name)}</b>
                  {court.group && !venue && <span className="text-dim shrink-0">· {court.group}</span>}
                  <span className="text-dim truncate min-w-0">{m ? matchContext(comp, m) : lastEnd ? t('frei seit {d}', { d: fmtDuration(now - lastEnd) }) : t('frei')}</span>
                  <span className={cx('ml-auto shrink-0', m ? 'text-live' : 'text-dim')}>{m?.startedAt ? fmtDuration(now - m.startedAt) : next ? t('bereit') : ''}</span>
                </header>
                {m ? (
                  <div className="p-4">
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                      <TeamBlock match={m} side="a" lookup={lookup} />
                      <div className="font-mono text-2xl text-dim">vs</div>
                      <TeamBlock match={m} side="b" lookup={lookup} right />
                    </div>
                    <LiveScore match={m} teamA={slotTeam(m.a, lookup)?.shortName ?? 'A'} teamB={slotTeam(m.b, lookup)?.shortName ?? 'B'} compact />
                    <div className="flex gap-2 mt-4">
                      <Button variant="primary" className="flex-1 md:flex-none min-h-11" onClick={() => setResultMatch(m)}>
                        {t('Ergebnis')}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={async () =>
                          (await confirmDialog(t('Spiel abbrechen und zurück in die Warteschlange?'), { ok: t('Spiel abbrechen'), danger: true })) && dispatch({ type: 'match.abort', matchId: m.id })
                        }
                        title={t('Feld freigeben, Spiel zurück in die Warteschlange')}
                      >
                        {t('Abbrechen')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4">
                    {next ? (
                      <>
                        <div className="label mb-1">
                          {t('Als nächstes')} · {matchContext(comp, next)}
                        </div>
                        <div className="text-[16px]">
                          <b>{slotLabel(next.a, lookup, allMatches)}</b> <span className="text-dim">{t('gegen')}</span> <b>{slotLabel(next.b, lookup, allMatches)}</b>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted text-sm">{open > 0 ? t('Kein startbares Spiel — Teams spielen noch oder Pause läuft.') : t('Nichts mehr offen.')}</div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button variant="primary" className="flex-1 md:flex-none min-h-11" disabled={!next} onClick={() => startOnCourt(court)}>
                        <Play size={14} /> {t('Spiel starten')}
                      </Button>
                      <Button onClick={() => setPickFor(court)} disabled={open === 0}>
                        {t('Anderes Spiel')}
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <WhoPlaysWhen
          tournament={tournament}
          comp={comp}
          forecast={forecast}
          venueFilter={onVenue}
          courtIds={courtIds}
          onTeam={(id) => {
            sucheTeam(lookup(id)?.name ?? '', id);
            requestAnimationFrame(zuTeamPartien);
          }}
        />

        <TeamPartien comp={comp} tournament={tournament} zuletzt={finished} forecast={forecast} suche={partienSuche.q} teamId={partienSuche.teamId} onSuche={sucheTeam} onMatch={setResultMatch} />
      </div>

      <aside className="md:sticky md:top-[60px]" data-tour="spiele-warteschlange">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="label">{venue ? t('Warteschlange · Platz {v}', { v: venue }) : t('Warteschlange')}</span>
          <span className="ml-auto font-mono text-sm">{queue.length}</span>
          {/* Am Turniertisch will man die ganze Liste sehen, nicht die schmale Spalte */}
          {queue.length > 0 && (
            <button className="label text-dim hover:text-ink cursor-pointer" onClick={() => setQueueBig(true)} title={t('Warteschlange groß anzeigen')}>
              <Maximize2 size={12} className="inline" /> {t('groß')}
            </button>
          )}
        </div>
        <div className="grid gap-1.5 md:max-h-[calc(100vh-140px)] md:overflow-y-auto pr-1">
          {queue.length === 0 && <div className="text-sm text-dim">{t('Leer.')}</div>}
          {queue.map((e, i) => {
            const f = eta(e.match);
            return (
              <MatchRow
                key={e.match.id}
                match={e.match}
                muted={!e.startable}
                wrap
                right={
                  <div className="flex flex-col items-end gap-1">
                    {e.startable ? <span className="label text-live">{t('bereit')}</span> : <span className="label text-warn text-right max-w-[110px] leading-tight">{tm(e.blockedBy ?? '')}</span>}
                    {f && !e.startable && (
                      <span className="font-mono text-[11px] text-dim whitespace-nowrap" title={t('voraussichtlich {c}', { c: f.court ?? '' })}>
                        {f.time}
                      </span>
                    )}
                    {pfeile(e, i)}
                  </div>
                }
              />
            );
          })}
        </div>
        <p className="text-[11px] text-dim mt-2 font-mono">{t('Pfeile = eins hoch/runter, mit Strich ganz nach vorn/ans Ende · Feld frei → oberstes bereites Spiel · ~Zeit = voraussichtlich')}</p>
      </aside>

      {resultMatch && <ResultDialog match={resultMatch} onClose={() => setResultMatch(null)} />}
      {/* Die ganze Warteschlange in voller Breite — am Turniertisch will man
          alle Paarungen lesen, nicht die abgeschnittene Spalte. */}
      <Dialog open={queueBig} onClose={() => setQueueBig(false)} title={venue ? t('Warteschlange · Platz {v}', { v: venue }) : t('Warteschlange')} width="max-w-4xl">
        <div className="grid gap-1.5">
          {queue.length === 0 && <div className="text-sm text-dim">{t('Leer.')}</div>}
          {queue.map((e, i) => {
            const f = eta(e.match);
            return (
              <MatchRow
                key={e.match.id}
                match={e.match}
                muted={!e.startable}
                right={
                  <div className="flex items-center gap-3 shrink-0">
                    {f && !e.startable && <span className="font-mono text-[12px] text-dim whitespace-nowrap">{f.time}</span>}
                    {e.startable ? <span className="label text-live">{t('bereit')}</span> : <span className="label text-warn">{tm(e.blockedBy ?? '')}</span>}
                    {pfeile(e, i)}
                  </div>
                }
              />
            );
          })}
        </div>
      </Dialog>
      {pauseOpen && (
        <Dialog open onClose={() => setPauseOpen(false)} title={t('Pause')} width="max-w-md">
          <div className="grid gap-3 text-sm">
            <p className="m-0 text-muted">{t('Während der Pause weist Airmail keine Spiele zu; laufende Spiele können fertig gespielt werden. „Wann spielt wer?“ rechnet mit dem Ende der Pause.')}</p>
            <div className="flex gap-2 flex-wrap">
              {[15, 30, 45, 60, 90].map((min) => (
                <Button key={min} onClick={() => startPause(min)}>
                  {t('{n} min', { n: min })}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="label">{t('oder bis')}</span>
              <Input type="time" className="w-32" value={pauseUntil} onChange={(e) => setPauseUntil(e.target.value)} />
              <Button variant="primary" onClick={() => startPause()} disabled={!pauseUntil}>
                {t('Pause starten')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
      {pickFor && (
        <Dialog open onClose={() => setPickFor(null)} title={t('{court}: Spiel wählen', { court: feldName(pickFor.name) })} width="max-w-xl">
          <div className="grid gap-1.5">
            {queue.filter((e) => e.startable).length === 0 && <div className="text-sm text-dim">{t('Kein startbares Spiel.')}</div>}
            {queue.map((e) => {
              const f = eta(e.match);
              return (
                <MatchRow
                  key={e.match.id}
                  match={e.match}
                  muted={!e.startable}
                  onClick={
                    e.startable || e.blockedBy === 'Pause'
                      ? () => {
                          dispatch({ type: 'match.start', matchId: e.match.id, courtId: pickFor.id });
                          setPickFor(null);
                        }
                      : undefined
                  }
                  right={
                    !e.startable ? (
                      <span className="label text-warn">
                        {tm(e.blockedBy ?? '')}
                        {f ? ` · ${f.time}` : ''}
                      </span>
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        </Dialog>
      )}
    </div>
  );
}

function TeamBlock({ match, side, lookup, right }: { match: Match; side: 'a' | 'b'; lookup: ReturnType<typeof teamLookup>; right?: boolean }) {
  const t = useT();
  const team = slotTeam(match[side], lookup);
  return (
    <div className={cx('min-w-0', right && 'text-right')}>
      <div className="font-bold text-[17px] tracking-tight truncate">{team?.name ?? '?'}</div>
      {/* Kleine Zeile unter dem Teamnamen: die Spieler, nicht etwa ein zweites Team */}
      <div className="text-xs text-dim truncate" title={t('Spieler')}>
        {team?.players.map((p) => `${p.firstName ? p.firstName[0] + '. ' : ''}${p.lastName}`).join(' · ')}
      </div>
      {match.result && <div className="font-mono text-muted text-sm">{scoreText(match)}</div>}
    </div>
  );
}
